import { zodResolver } from '@/utils/zodResolver'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'

import { AuthLayout } from '@ankor-io/ui-components/src/AuthLayout'
import { Button } from '@ankor-io/ui-components/src/Button'
import { ErrorMessage, Field, Label } from '@ankor-io/ui-components/src/Fieldset'
import { Heading } from '@ankor-io/ui-components/src/Heading'
import { Input } from '@ankor-io/ui-components/src/Input'
import { Text } from '@ankor-io/ui-components/src/Text'

import { useConfig } from '@/providers/config'

const formSchema = z.object({
  email: z.string().email({
    message: 'Invalid email address.',
  }),
})

const SignInPage = () => {
  const { config } = useConfig()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { login, isAuthenticated } = useKindeAuth()
  const email = searchParams.get('email')

  const [doesAccountExist, setDoesAccountExist] = useState(true)
  const [checkFailed, setCheckFailed] = useState(false)
  const [lastSubmittedEmail, setLastSubmittedEmail] = useState<string | null>(null)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: email || '',
    },
  })

  // if the user is already authenticated, redirect them to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, navigate])

  // Reset the submit errors when email field is updated
  useEffect(() => {
    const currentEmail = form.watch('email')
    if (lastSubmittedEmail && currentEmail !== lastSubmittedEmail && (!doesAccountExist || checkFailed)) {
      setDoesAccountExist(true)
      setCheckFailed(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.watch('email'), lastSubmittedEmail])

  const handleDoesAccountExist = async (email: string) => {
    if (!email?.trim()) {
      setDoesAccountExist(false)
      return false
    }

    const response = await fetch(`/api/iam/account/check`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    })

    if (response.ok) {
      setDoesAccountExist(true)
      return true
    }

    // Only a 404 means the account is missing. Anything else is a server-side
    // failure and must not be reported to the user as an unknown email.
    if (response.status === 404) {
      setDoesAccountExist(false)
      return false
    }

    throw new Error(`Account check failed with status ${response.status}`)
  }

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setCheckFailed(false)
      setDoesAccountExist(true)
      setLastSubmittedEmail(values.email)
      const accountExists = await handleDoesAccountExist(values.email)
      if (!accountExists) {
        console.error('Account does not exist')
        return
      }

      // Make request to Kinde to signin
      const loginOptions = {
        orgCode: config?.kinde.org,
        connectionId: config?.kinde.connectionId,
        loginHint: values.email,
      }

      login(loginOptions)
    } catch (error) {
      console.error(error)
      setCheckFailed(true)
    }
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-md space-y-6">
        <div className="space-y-2 text-center">
          <Heading level={1}>Welcome Back</Heading>
          <Text>Enter your credentials to sign in to your account</Text>
        </div>

        <form
          role="form"
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-4"
        >
          <Field>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="john@example.com"
              {...form.register('email')}
            />
            {form.formState.errors.email && <ErrorMessage>{form.formState.errors.email.message}</ErrorMessage>}
          </Field>

          <Button
            type="submit"
            className="w-full"
            disabled={form.formState.isSubmitting || !form.formState.isValid}
            color="primary"
          >
            {form.formState.isSubmitting ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <div className="text-center">
          {!doesAccountExist && (
            <Field>
              <ErrorMessage className="text-red-500 mb-2">No account found with this email address.</ErrorMessage>
            </Field>
          )}

          {checkFailed && (
            <Field>
              <ErrorMessage className="text-red-500 mb-2">
                Something went wrong on our end. Please try again in a moment.
              </ErrorMessage>
            </Field>
          )}

          {/* <Button
            plain
            disabled={form.formState.isSubmitting}
            onClick={() => navigate('/sign-up')}
          >
            Don't have an account? Sign up
          </Button> */}
        </div>
      </div>
    </AuthLayout>
  )
}

export default SignInPage
